
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Search as SearchIcon, Info as Info } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService'; // Ensure postData is defined
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import { Tooltip } from '@mui/material';


function PurchaseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const branchId = 0;
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchtableData();
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const fetchtableData = async () => {
        fetchData('PurchaseApi', `/${fromDate}/${toDate}/${companyId}/${branchId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Purchase Details:', error);
                if (error.response && error.response.status === 401) {
                    logout();
                    history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
                }
            });
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );
    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };


    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };
    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFromDate(selectedDate);
    };

    // Similar function for toDate
    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setToDate(selectedDate);
    };

    const handleDelete = async () => {
        deleteData('PurchaseApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('PurchaseApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const openImportDialog = () => {
        setImportDialogOpen(true);
    };

    const closeImportDialog = () => {
        setImportDialogOpen(false);
        setFileData([]);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const reader = new FileReader();


        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const rowsToRead = sheetData.slice(9); // Read from the 9th row

            const formattedData = rowsToRead
                .filter(row => row[2]) // Skip rows where row[2] (BillingDate) is blank
                .map((row) => {
                    let billingDate = row[2];
                    // Convert "DD.MM.YYYY" to ISO 8601 format
                    if (billingDate) {
                        const [day, month, year] = billingDate.split('.');
                        const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
                        billingDate = !isNaN(date) ? date.toISOString() : null;
                    }

                    return {
                        BillingDate: billingDate,
                        BillDoc: row[4] ? String(row[4]) : "", // Ensure BillDoc is a string
                        Payer: row[5] ? String(row[5]) : "", // Ensure Payer is a string
                        Name: row[6] ? String(row[6]) : "", // Ensure Name is a string
                        Item: row[7] ? String(row[7]) : "", // Ensure Item is a string
                        Material: row[8] ? String(row[8]) : "", // Ensure Material is a string
                        Description: row[9] ? String(row[9]) : "", // Ensure Description is a string
                        ProductHierarchy: row[10] ? String(row[10]) : "", // Ensure ProductHierarchy is a string
                        Status: row[11] ? String(row[11]) : "", // Ensure Status is a string
                        Qty: row[12] ?? 0,
                        BilledQuantity: row[13] ?? 0,
                        RRP: row[14] ?? 0,
                        ZV05: row[15] ?? 0,
                        ConcessionFee: row[16] ?? 0,
                        NetSalesII: row[17] ?? 0,
                        Vat: row[18] ?? 0,
                        GrossSales: row[19] ?? 0
                    };
                });

            setFileData(formattedData);

        };

        reader.readAsArrayBuffer(file);
    };

    const handleImport = () => {

        setIsLoading(true);

        const CreateData = {
            CompanyId: companyId,
            BranchId: branchId,
            CreatedBy: userId,
            PurchaseImport: fileData
        };

        postData('PurchaseImportApi', CreateData, token)
            .then((response) => {
                fetchtableData();
                setIsLoading(false);
                closeImportDialog();
                console.log('Data posted successfully:', response);
            })
            .catch((error) => {
                console.error('Error posting data:', error);
                setIsLoading(false);
            });
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    return (
        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>
            <div className='w-full mb-2 flex'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Purchase </h6>
                <div style={{ marginTop: '10px', marginLeft: '12px' }}>
                    <input
                        className="shadow text-gray-700 leading-tight"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? '300px' : '200px',
                            height: '30px',
                            border: '1px solid #ccc',
                            borderRadius: '15px',
                            padding: '0 20px',
                            transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                            outline: 'none',
                            borderColor: isFocused ? 'darkcyan' : '#ccc',
                            boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                            marginTop: '10px',
                            marginLeft: '12px',

                        }}
                    />
                </div>
                


                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px', marginTop: '-7px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        From Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px', marginTop: '-7px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        To Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>

                


                <Tooltip
                    title="Refresh For Data Load"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: 'darkolivegreen',
                                color: 'white',
                                border: '1px solid white',
                                fontWeight: 'bold',
                                boxShadow: 2,
                            },
                        },
                    }}
                >
                    <Button
                        style={{ backgroundColor: '#C9E9D2', color: "black", height: '36px', marginLeft: '10px', marginTop: '13px' }}
                        onClick={fetchtableData}
                        className="linkcolor"
                    >
                        <span>Refresh</span>
                    </Button>
                </Tooltip>

                {hasCreateMasterPermission && (
                    <Tooltip title="Create Purchase">
                        <Button
                            style={{
                                background: 'linear-gradient(to right, #16423C,#6A9C89)',
                                //padding: '10px 20px',
                                color: 'white',
                                fontWeight: 'bolder',
                                textTransform: 'none',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                                maxHeight: '35px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '8px',
                                marginTop: '13px'
                            }}
                            component={Link}
                            to="/PurchaseCreate"
                        >
                            Create
                        </Button>
                    </Tooltip>
                )}


            </div>

            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: 'teal' }}>
                            <TableRow>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '9%' }}>Bill Date</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '9%' }}>Bill Number</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '20%' }}>Supplier Name</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>Amount</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>CGST</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>SGST</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>IGST</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>Total</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>Quantity</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: 'white' }}>
                            {filteredData.map(item => (
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'}>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '9%' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '9%' }}>{item.BillNumber}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '20%' }}>{item.SupplierName}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '7%', textAlign: 'center' }}>{item.Amount}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '7%', textAlign: 'center' }}>{item.CGSTAmount}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '7%', textAlign: 'center' }}>{item.SGSTAmount}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '7%', textAlign: 'center' }}>{item.IGSTAmount}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '7%', textAlign: 'center' }}>{item.TotalAmount}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '7%', textAlign: 'center' }}>{item.TotalQuantity}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '10%', textAlign: 'right' }}>

                                        <IconButton component={Link} to={`/PurchaseDetails/${item.Id}`} color="warning">
                                            <Info />
                                        </IconButton>

                                        {hasEditMasterPermission && item.IsActive && (
                                            <IconButton component={Link} to={`/PurchaseEdit/${item.Id}`} color="primary">
                                                <EditIcon />
                                            </IconButton>
                                        )}
                                        {hasDeleteMasterPermission && item.IsActive && (
                                            <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                        {!item.IsActive && (
                                            <IconButton onClick={() => handleActivate(item.Id)} color="success">
                                                <ActivateIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Confirmation Dialog */}

            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <button
                        onClick={closeConfirmDialog}
                        style={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            marginRight: '8px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#ffffff',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                        }}
                    >
                        Confirm
                    </button>
                </DialogActions>

            </Dialog>

            {/* Import Dialog */}
            <Dialog
                open={importDialogOpen}
                onClose={closeImportDialog}
                aria-labelledby="import-dialog-title"
                aria-describedby="import-dialog-description"
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle id="import-dialog-title">Import Purchase</DialogTitle>
                <DialogContent>
                    <DialogContentText id="import-dialog-description">
                        Select an Excel file to import purchase data. The data will be previewed before importing.
                    </DialogContentText>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        style={{ marginTop: '16px' }}
                    />
                    {fileData.length > 0 && (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: 'teal' }}>
                                    <TableRow>
                                        {/* Add table headers for the preview data */}
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Billing Date</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Bill Doc</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Payer</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Name</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Item</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Material</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Description</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Product Hierarchy</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Status</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Qty</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Billed Quantity</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>RRP</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>ZV05</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Concession Fee</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Net Sales II</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Vat</TableCell>
                                        <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', width: '15%' }}>Gross Sales</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ backgroundColor: 'white' }}>
                                    {fileData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{formatDate(row.BillingDate)}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.BillDoc}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Payer}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Name}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Item}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Material}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Description}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.ProductHierarchy}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Status}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Qty}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.BilledQuantity}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.RRP}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.ZV05}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.ConcessionFee}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.NetSalesII}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.Vat}</TableCell>
                                            <TableCell sx={{ height: '30px', padding: '5px', width: '15%' }}>{row.GrossSales}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={closeImportDialog} color="primary">Cancel
                    </Button>

                    <Button variant="contained" onClick={handleImport} color="secondary" disabled={isLoading || fileData.length === 0}>
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Import"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default PurchaseIndex;



